/**
 * FormFieldBlockchain — Blockchain-specific field components.
 *
 * Handles: chain selector, multi-chain selector, network selector, token selector,
 * token addresses (multiple), address input, recipient address, contract address,
 * contract project selector, constructor args, event ABI, function ABI, args,
 * functionName visibility, token amount.
 */
import { formatLabel } from "@/utils/string";
import { SOLANA_MAINNET_CHAIN_ID, SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isSolanaNodeType } from "@/lib/utils/chain-family";
import * as React from "react";
import AddressInput from "./AddressInput";

// Hoisted to avoid new array reference on every render (used in ChainSelector excludeChainIds)
const EXCLUDE_SOLANA_CHAIN_IDS = [...SOLANA_CHAIN_IDS];
import ArgsInput from "./ArgsInput";
import ChainSelector from "./ChainSelector";
import ConstructorArgsInput from "./ConstructorArgsInput";
import ContractAddressInput from "./ContractAddressInput";
import ContractProjectSelector from "./ContractProjectSelector";
import EventAbiSelector from "./EventAbiSelector";
import FunctionAbiSelector from "./FunctionAbiSelector";
import AssetSelector from "./AssetSelector";
import HyperliquidAssetAmountInput from "./HyperliquidAssetAmountInput";
import HyperliquidCoinSelector from "./HyperliquidCoinSelector";
import { coinGeckoPickerProps } from "@/hooks/useCoinGeckoSearch";
import MorphoVaultSelector from "./MorphoVaultSelector";
import MultiChainSelector from "./MultiChainSelector";
import RecipientAddressInput from "./RecipientAddressInput";
import TokenAmountInput from "./TokenAmountInput";
import TokenSelector from "./TokenSelector";
import type { FormFieldInternalProps } from "./FormField";

/**
 * Attempts to render a blockchain-specific field component.
 * Returns a React element if the field matches, or `null` to indicate
 * the caller should continue to the next category.
 */
export function renderBlockchainField({
  name,
  property,
  value,
  onChange,
  handleChange,
  allValues,
  currentNodeId,
  currentNodeType,
  allNodes,
  allEdges,
  hideLabel,
  nodeDefinitions,
  schemaProperties,
  currentNodePayload,
  workflowWalletAddress,
  showAnySpendFundingCta,
  validationData,
  isEvmReadWrite,
  isProjectMode,
  abiSelectorMode,
  setAbiSelectorMode,
  withVariableToggle,
}: FormFieldInternalProps & {
  handleChange: (value: unknown) => void;
  validationData: Record<string, unknown>;
  isEvmReadWrite: boolean;
  isProjectMode: boolean;
  abiSelectorMode: "auto" | "manual";
  setAbiSelectorMode: React.Dispatch<React.SetStateAction<"auto" | "manual">>;
  withVariableToggle: (element: React.ReactElement) => React.ReactElement;
}): React.ReactElement | null {
  const isSolana = isSolanaNodeType(currentNodeType ?? "", nodeDefinitions);

  const schemaHasHyperliquidCoin =
    schemaProperties?.coin?.format === "hyperliquid-asset" || schemaProperties?.coin?.type === "hyperliquid-asset";
  if (
    (name === "dex" || name === "size" || name === "price" || name === "side" || name === "orderType") &&
    schemaHasHyperliquidCoin &&
    !!schemaProperties?.size
  ) {
    return <></>;
  }

  // Prefer the explicit prop (e.g. AI chat ask-block passing the selected wallet),
  // otherwise fall back to the current node's connector wallet (workflow editor).
  const resolvedWalletAddress =
    workflowWalletAddress ??
    (((currentNodePayload as Record<string, unknown>)?.connector as Record<string, unknown> | undefined)
      ?.walletAddress as string | undefined);

  // Check if the schema has a token field — if so, the chain selector is baked into the token selector.
  // Only match on schema type/format annotations, NOT on field name. Plain string fields named
  // "tokenAddress" (e.g. trigger filter inputs) do NOT render a TokenSelector, so they must NOT
  // suppress the standalone chain picker.
  const schemaHasTokenField = Object.entries(schemaProperties ?? {}).some(
    ([, prop]) =>
      (prop as Record<string, unknown>).type === "token-address" ||
      (prop as Record<string, unknown>).format === "token-address",
  );

  // Check if the schema has a morpho-vault field — if so, the chain selector is baked into the vault selector
  const schemaHasMorphoVaultField = Object.entries(schemaProperties ?? {}).some(
    ([, prop]) => (prop as Record<string, unknown>).format === "morpho-vault",
  );

  // Chain selector fields - use ChainSelector
  // Detected via schema format: "chain-id" (or legacy field name "chainId")
  // If the schema has `enum`, pass it as allowedChainIds to filter the chain picker
  // while preserving the rich UI (icons, names, search). E.g. Railgun restricts to 4 chains.
  // Note: `enumNames` in the schema is for documentation/tooling only — ChainSelector
  // gets display names from the chain list API, not from schema labels.
  if (property.type === "chain-id" || property.format === "chain-id" || name === "chainId") {
    // Hide the primary source chain selector when a token or morpho-vault field exists —
    // chain filter is built into the respective selector. Only hide the exact "chainId" field;
    // destination/output chain fields (e.g. dstChainId, destinationChainId) must remain visible.
    if ((schemaHasTokenField || schemaHasMorphoVaultField) && name === "chainId") {
      return <></>;
    }
    // Runtime-check that enum is actually number[] before passing (SchemaProperty.enum is unknown[])
    const numericEnum =
      Array.isArray(property.enum) && property.enum.every((v: unknown) => typeof v === "number")
        ? (property.enum as number[])
        : undefined;
    // Exclude Solana chains from EVM-only actions (and vice versa when enum is set)
    const excludeIds = !numericEnum && !isSolana ? EXCLUDE_SOLANA_CHAIN_IDS : undefined;
    return withVariableToggle(
      <ChainSelector
        label={hideLabel ? "" : property.title || "Chain"}
        description={hideLabel ? "" : property.description || ""}
        value={value}
        onChange={handleChange}
        allowedChainIds={numericEnum}
        excludeChainIds={excludeIds}
      />,
    );
  }

  // Network selector field - use ChainSelector with networkId output format
  // Field name "network" outputs string network IDs like 'eth', 'bsc', 'polygon_pos'
  if (property.type === "network" || (name === "network" && property.type === "string")) {
    return withVariableToggle(
      <ChainSelector
        label={hideLabel ? "" : property.title || "Network"}
        description={hideLabel ? "" : property.description || ""}
        value={value}
        onChange={handleChange}
        valueFormat="networkId"
      />,
    );
  }

  // Multi-chain selector fields - use MultiChainSelector
  // Detected via schema format: "chain-ids" (or legacy field name "chainIds" with array type)
  // Same enum → allowedChainIds pattern as single chain selector above.
  if (
    property.type === "chain-ids" ||
    property.format === "chain-ids" ||
    (name === "chainIds" && property.type === "array")
  ) {
    const numericEnumMulti =
      Array.isArray(property.enum) && property.enum.every((v: unknown) => typeof v === "number")
        ? (property.enum as number[])
        : undefined;
    return withVariableToggle(
      <MultiChainSelector
        label={hideLabel ? "" : property.title || "Chains"}
        description={hideLabel ? "" : property.description}
        value={Array.isArray(value) ? value : []}
        onChange={handleChange}
        allowedChainIds={numericEnumMulti}
      />,
    );
  }

  // Networks selector field - use MultiChainSelector with networkId output format
  // Field name "networks" with array type outputs string network IDs
  if (property.type === "networks" || (name === "networks" && property.type === "array")) {
    return withVariableToggle(
      <MultiChainSelector
        label={hideLabel ? "" : property.title || "Networks"}
        description={hideLabel ? "" : property.description}
        value={Array.isArray(value) ? value : []}
        onChange={handleChange}
        valueFormat="networkId"
      />,
    );
  }

  // Contract project selector - use ContractProjectSelector
  // Detected via schema format: "contract-project" (or legacy field name "contractProjectId")
  // This unified selector handles both project selection AND custom bytecode mode
  if (property.type === "contract-project" || property.format === "contract-project" || name === "contractProjectId") {
    return (
      <ContractProjectSelector
        label={hideLabel ? "" : "Contract Source"}
        description={
          hideLabel
            ? ""
            : isEvmReadWrite
              ? "Select a deployed contract project or choose custom to enter address and ABI manually"
              : "Select a contract project or choose custom bytecode"
        }
        value={value}
        onChange={handleChange}
        contractSource={allValues.contractSource as string | undefined}
        onContractSourceChange={source => onChange("contractSource", source)}
        onProjectAbiChange={abi => onChange("_projectAbi", abi)}
        requireBytecode={!isEvmReadWrite}
        onChainIdChange={isEvmReadWrite ? chainId => onChange("chainId", chainId) : undefined}
        selectedChainId={isEvmReadWrite ? (allValues.chainId as number | undefined) : undefined}
        onContractAddressChange={isEvmReadWrite ? address => onChange("contractAddress", address) : undefined}
      />
    );
  }

  // Hide contractSource and _projectAbi fields - managed internally
  if (name === "contractSource" || name === "_projectAbi") {
    // Return a fragment to signal "handled" (renders nothing)
    return <></>;
  }

  // Hide bytecode and abi fields when using project mode (not custom)
  // Show only when contractSource is "custom" or undefined (for backwards compatibility)
  if (name === "bytecode") {
    const contractSource = allValues.contractSource;
    if (contractSource === "project") {
      return <></>;
    }
  }

  // Hide contractAddress when using project mode for evm-read/evm-write
  // The address is resolved from the contract project's deployment at execution time
  if (name === "contractAddress") {
    if (isProjectMode && isEvmReadWrite) {
      return <></>;
    }
    // For evm-log triggers, the schema now advertises both the legacy scalar
    // `contractAddress` and the new array `contractAddresses`. Showing both
    // side-by-side confuses users ("which one do I fill?"). Hide the scalar
    // when the schema offers the array unless a legacy value exists on the
    // current workflow (preserve editability of existing single-contract
    // workflows).
    const hasAddressesField =
      schemaProperties && Object.prototype.hasOwnProperty.call(schemaProperties, "contractAddresses");
    const hasLegacyValue = typeof value === "string" && value.trim() !== "";
    if (hasAddressesField && !hasLegacyValue) {
      return <></>;
    }
  }

  // Constructor args - use smart input that detects params from ABI
  if (property.type === "constructor-args" || name === "constructorArgs") {
    const contractSource = allValues.contractSource as string | undefined;
    const contractProjectId = allValues.contractProjectId as string | undefined;
    const projectAbi = allValues._projectAbi as string | undefined;
    const customAbi = allValues.abi as string | undefined;

    return (
      <ConstructorArgsInput
        label={hideLabel ? "" : property.title || "Constructor Arguments"}
        description={hideLabel ? "" : property.description}
        value={value || ""}
        onChange={handleChange}
        contractSource={contractSource}
        contractProjectId={contractProjectId}
        abiString={contractSource === "project" ? projectAbi : customAbi}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
      />
    );
  }

  if (property.type === "contract-address" || property.format === "contract-address" || name === "contractAddress") {
    const chainFieldName = (property as Record<string, unknown>)["x-chain-field"] || "chainId";
    let resolvedChainId = validationData[chainFieldName as string] || allValues[chainFieldName as string];
    if (resolvedChainId == null) {
      const chainKey = Object.keys(allValues).find(
        k => schemaProperties?.[k]?.type === "chain-id" || schemaProperties?.[k]?.format === "chain-id",
      );
      if (chainKey) resolvedChainId = allValues[chainKey];
    }
    return withVariableToggle(
      <ContractAddressInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value || ""}
        onChange={handleChange}
        required={false}
        chainId={resolvedChainId != null ? Number(resolvedChainId) : undefined}
        allNodes={allNodes}
        popoverModal
      />,
    );
  }

  // Morpho vault selector - use MorphoVaultSelector for vault address selection
  // Detected via schema format: "morpho-vault"
  if (property.format === "morpho-vault") {
    const chainFieldName = property["x-chain-field"] || "chainId";
    const rawChainId = validationData[chainFieldName] || allValues[chainFieldName];
    const resolvedChainId = rawChainId != null ? Number(rawChainId) : undefined;

    return withVariableToggle(
      <MorphoVaultSelector
        label={hideLabel ? "" : property.title || "Vault"}
        description={hideLabel ? "" : property.description || "Select a Morpho vault"}
        value={value || ""}
        onChange={handleChange}
        chainId={resolvedChainId != null && !isNaN(resolvedChainId) ? resolvedChainId : undefined}
        showChainFilter
        onChainIdChange={cId => onChange(chainFieldName, cId)}
      />,
    );
  }

  // Coin ID picker — fires on explicit format hint OR by field name on coingecko-* nodes,
  // so workflows render the picker even before the backend reloads the regenerated schema.
  const isCoinIdField =
    property.format === "coin-id" ||
    ((name === "coinId" || name === "coinIds") && (currentNodeType ?? "").startsWith("coingecko-"));
  if (isCoinIdField) {
    const isMulti = property.type === "array";
    return withVariableToggle(
      <TokenSelector
        {...coinGeckoPickerProps}
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={
          isMulti
            ? Array.isArray(value)
              ? (value as string[])
              : value
                ? [value as string]
                : []
            : ((value as string) ?? "")
        }
        onChange={handleChange}
        required={property.required || false}
        multiple={isMulti}
      />,
    );
  }

  if (property.format === "stork-asset") {
    return withVariableToggle(
      <AssetSelector
        label={hideLabel ? "" : property.title || "Asset"}
        description={hideLabel ? "" : property.description || "Select an asset pair"}
        value={value || ""}
        onChange={handleChange}
        required={false}
      />,
    );
  }

  if (property.format === "hyperliquid-asset" || property.type === "hyperliquid-asset") {
    const hasSizeField = !!schemaProperties?.size;
    if (hasSizeField) {
      const hasPriceField = !!schemaProperties?.price;
      const hasSideField = !!schemaProperties?.side;
      const hasOrderTypeField = !!schemaProperties?.orderType;
      return withVariableToggle(
        <HyperliquidAssetAmountInput
          coin={(value as string) || ""}
          dex={(allValues.dex as string) || ""}
          size={(allValues.size as string) || ""}
          onCoinChange={v => onChange(name, v)}
          onDexChange={v => onChange("dex", v)}
          onSizeChange={v => onChange("size", v)}
          side={hasSideField ? (allValues.side as string) || "buy" : undefined}
          onSideChange={hasSideField ? v => onChange("side", v) : undefined}
          orderType={hasOrderTypeField ? (allValues.orderType as string) || "limit" : undefined}
          onOrderTypeChange={hasOrderTypeField ? v => onChange("orderType", v) : undefined}
          price={hasPriceField ? (allValues.price as string) || "" : undefined}
          onPriceChange={hasPriceField ? v => onChange("price", v) : undefined}
          label={hideLabel ? "" : hasPriceField ? "Order" : "Asset & Size"}
          popoverModal
        />,
      );
    }
    const dexValue = (allValues.dex as string) || undefined;
    return withVariableToggle(
      <HyperliquidCoinSelector
        label={hideLabel ? "" : property.title || "Asset"}
        description={hideLabel ? "" : property.description || "Select a Hyperliquid perpetual market"}
        value={(value as string) || ""}
        onChange={handleChange}
        required={property.required || false}
        dex={dexValue}
      />,
    );
  }

  // Multiple token addresses field - use TokenSelector with multiple mode
  // Field name "addresses" with array type indicates multi-token selection
  if (property.type === "token-addresses" || (name === "addresses" && property.type === "array")) {
    // Check if schema has chain fields
    const hasChainField = !!schemaProperties?.chainId || !!schemaProperties?.chainIds;

    // Determine which chain field this token is paired with
    const customChainField = property["x-chain-field"];
    const schemaHasChainIds = !!schemaProperties?.chainIds && !schemaProperties?.chainId;
    const chainFieldName = customChainField || (schemaHasChainIds ? "chainIds" : "chainId");
    const chainIdValue = validationData[chainFieldName] || allValues[chainFieldName];

    // Get chainIds array if available (for multi-chain filtering)
    const chainIdsFieldName = (property as Record<string, unknown>)["x-chain-ids-field"] || "chainIds";
    const chainIdsValue = validationData[chainIdsFieldName as string] || allValues[chainIdsFieldName as string];
    const chainIdsArray =
      Array.isArray(chainIdValue) && chainIdValue.length > 0
        ? chainIdValue
        : Array.isArray(chainIdsValue) && chainIdsValue.length > 0
          ? chainIdsValue
          : undefined;

    // Handle chain change - only if schema has chain fields
    const handleChainIdChange = hasChainField
      ? (chainId: number) => {
          if (chainFieldName === "chainIds") {
            onChange(chainFieldName, [chainId]);
          } else {
            onChange(chainFieldName, chainId);
          }
        }
      : undefined;

    return withVariableToggle(
      <TokenSelector
        label={hideLabel ? "" : property.title || "Token Addresses"}
        description={hideLabel ? "" : property.description || "Select one or more tokens"}
        value={Array.isArray(value) ? value : value ? [value] : []}
        onChange={handleChange}
        onChainIdChange={handleChainIdChange}
        required={property.required || false}
        chainId={Array.isArray(chainIdValue) ? chainIdValue[0] : chainIdValue}
        chainIds={chainIdsArray}
        workflowWalletAddress={resolvedWalletAddress}
        showAnySpendFundingCta={showAnySpendFundingCta}
        multiple={true}
      />,
    );
  }

  // Token address fields - use TokenSelector with Relay API
  // Detected via schema format: "token-address" (or legacy field name "tokenAddress")
  // Supports "x-chain-field" to specify which field controls the chain (defaults to "chainId")
  if (property.type === "token-address" || property.format === "token-address" || name === "tokenAddress") {
    // Determine which chain field this token is paired with
    // Priority: x-chain-field > schema has chainIds > default "chainId"
    const customChainField = property["x-chain-field"];
    const schemaHasChainIds = !!schemaProperties?.chainIds && !schemaProperties?.chainId;
    const chainFieldName = customChainField || (schemaHasChainIds ? "chainIds" : "chainId");
    const chainIdValue = validationData[chainFieldName] || allValues[chainFieldName];

    // Get chainIds array if available (for multi-chain filtering)
    const chainIdsFieldName = (property as Record<string, unknown>)["x-chain-ids-field"] || "chainIds";
    const chainIdsValue = validationData[chainIdsFieldName as string] || allValues[chainIdsFieldName as string];
    // Use chainIdValue if it's already an array (paired with chainIds field), otherwise fall back to separate chainIds field
    const chainIdsArray =
      Array.isArray(chainIdValue) && chainIdValue.length > 0
        ? chainIdValue
        : Array.isArray(chainIdsValue) && chainIdsValue.length > 0
          ? chainIdsValue
          : undefined;

    // Handle chain change - wrap in array if target field is chainIds
    const handleChainIdChange = (chainId: number) => {
      if (chainFieldName === "chainIds") {
        onChange(chainFieldName, [chainId]);
      } else {
        onChange(chainFieldName, chainId);
      }
    };

    // Get allowedChainIds from the chain field's enum (if schema has one)
    const chainProperty = schemaProperties?.chainId || schemaProperties?.chainIds;
    const propEnum = (chainProperty as { enum?: unknown[] })?.enum;
    const chainEnum =
      Array.isArray(propEnum) && propEnum.every((v): v is number => typeof v === "number") ? propEnum : undefined;

    // Schema hints for filtering out incompatible tokens/chains
    const excludeNative = (property as Record<string, unknown>)["x-exclude-native"] === true;
    const evmOnly = (property as Record<string, unknown>)["x-evm-only"] === true;

    return withVariableToggle(
      <TokenSelector
        label={hideLabel ? "" : property.title || "Token"}
        description={hideLabel ? "" : property.description || "Search token or enter 0x address"}
        value={value || ""}
        onChange={handleChange}
        onChainIdChange={handleChainIdChange}
        required={property.required || false}
        chainId={Array.isArray(chainIdValue) ? chainIdValue[0] : chainIdValue}
        chainIds={chainIdsArray}
        workflowWalletAddress={resolvedWalletAddress}
        showAnySpendFundingCta={showAnySpendFundingCta}
        allowedChainIds={chainEnum}
        excludeNative={excludeNative}
        evmOnly={evmOnly}
      />,
    );
  }

  // Recipient address fields - use RecipientAddressInput with ENS support
  // Detected via schema format: "recipient-address" (or legacy field name "recipientAddress")
  if (property.type === "recipient-address" || property.format === "recipient-address" || name === "recipientAddress") {
    return withVariableToggle(
      <RecipientAddressInput
        label={hideLabel ? "" : property.title || "Recipient"}
        description={hideLabel ? "" : property.description}
        value={value || ""}
        onChange={handleChange}
        required={property.required || false}
        isSolana={isSolana}
        chainId={
          typeof allValues.chainId === "number"
            ? allValues.chainId
            : typeof validationData.chainId === "number"
              ? validationData.chainId
              : isSolana
                ? SOLANA_MAINNET_CHAIN_ID
                : undefined
        }
      />,
    );
  }

  // Generic address fields - use AddressInput with validation
  // Detected via schema format: "address" (or legacy field name pattern)
  const isAddressField =
    property.type === "address" ||
    property.format === "address" ||
    (name.toLowerCase().includes("address") &&
      name !== "contractAddress" &&
      name !== "recipientAddress" &&
      name !== "addressId" &&
      (property.type === "string" || !property.type));

  if (isAddressField) {
    const addressChainId =
      typeof allValues.chainId === "number" ? allValues.chainId : isSolana ? SOLANA_MAINNET_CHAIN_ID : undefined;
    return withVariableToggle(
      <AddressInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value || ""}
        onChange={handleChange}
        required={property.required || false}
        placeholder={isSolana ? "Enter a Solana address" : undefined}
        chainId={addressChainId}
      />,
    );
  }

  // Special case for eventAbi - use dedicated EventAbiSelector component.
  // Fall back to the first item of contractAddresses when the legacy scalar
  // contractAddress is empty — users using the new multi-contract shape
  // still need the ABI picker to unlock.
  if (property.type === "event-abi" || name === "eventAbi") {
    const contractAddressesArr = Array.isArray(allValues.contractAddresses)
      ? (allValues.contractAddresses as unknown[])
      : undefined;
    const firstFromArr = contractAddressesArr?.find((a): a is string => typeof a === "string" && a.trim() !== "");
    const effectiveContractAddress =
      (typeof allValues.contractAddress === "string" && allValues.contractAddress) || firstFromArr;
    return (
      <EventAbiSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value}
        onChange={handleChange}
        chainId={allValues.chainId}
        contractAddress={effectiveContractAddress}
      />
    );
  }

  // Special case for abi field (EVM Read/Write) - use FunctionAbiSelector
  // In custom mode: hide until chainId and contractAddress are provided
  // In project mode: show when _projectAbi is available (no contractAddress needed)
  if (property.type === "function-abi" || name === "abi") {
    const projectAbi = allValues._projectAbi as string | undefined;

    if (isProjectMode && isEvmReadWrite) {
      // Project mode: use preloaded ABI from contract project
      if (!projectAbi) {
        return <></>;
      }

      const componentKey = `${currentNodeId}-project-${allValues.contractProjectId}`;

      return (
        <FunctionAbiSelector
          key={componentKey}
          label={hideLabel ? "" : property.title || "Function ABI"}
          description={hideLabel ? "" : property.description}
          value={value}
          onChange={handleChange}
          chainId={validationData.chainId}
          contractAddress={validationData.contractAddress}
          preloadedAbi={projectAbi}
          includeWriteFunctions={currentNodeType === "evm-write"}
          onFunctionNameChange={functionName => onChange("functionName", functionName)}
          onModeChange={isManual => setAbiSelectorMode(isManual ? "manual" : "auto")}
        />
      );
    }

    // Custom mode: require chainId and contractAddress
    const hasChainId = validationData.chainId && typeof validationData.chainId === "number";
    const hasValidContractAddress =
      validationData.contractAddress &&
      typeof validationData.contractAddress === "string" &&
      (validationData.contractAddress as string).startsWith("0x") &&
      (validationData.contractAddress as string).length === 42;

    // Don't show until both prerequisites are met
    if (!hasChainId || !hasValidContractAddress) {
      return <></>;
    }

    // Force remount when chainId or contractAddress changes to prevent stale data
    const componentKey = `${currentNodeId}-${validationData.chainId}-${validationData.contractAddress}`;

    return (
      <FunctionAbiSelector
        key={componentKey}
        label={hideLabel ? "" : property.title || "Function ABI"}
        description={hideLabel ? "" : property.description}
        value={value}
        onChange={handleChange}
        chainId={validationData.chainId}
        contractAddress={validationData.contractAddress}
        includeWriteFunctions={currentNodeType === "evm-write"}
        onFunctionNameChange={functionName => onChange("functionName", functionName)}
        onModeChange={isManual => setAbiSelectorMode(isManual ? "manual" : "auto")}
      />
    );
  }

  // Special case for args - use ArgsInput with auto/manual modes
  if (property.type === "args" || name === "args") {
    if (isProjectMode && isEvmReadWrite) {
      // In project mode, show args when ABI is selected
      if (!validationData.abi) {
        return <></>;
      }
    } else {
      // In custom mode, hide until chain and contract are provided
      const hasChainId = validationData.chainId && typeof validationData.chainId === "number";
      const hasValidContractAddress =
        validationData.contractAddress &&
        typeof validationData.contractAddress === "string" &&
        (validationData.contractAddress as string).startsWith("0x") &&
        (validationData.contractAddress as string).length === 42;

      if (!hasChainId || !hasValidContractAddress) {
        return <></>;
      }
    }

    return (
      <ArgsInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value}
        onChange={handleChange}
        abi={validationData.abi}
      />
    );
  }

  // Hide functionName based on FunctionAbiSelector mode
  if (name === "functionName") {
    let prerequisitesMet = false;
    if (isProjectMode && isEvmReadWrite) {
      prerequisitesMet = true;
    } else {
      const hasChainId = validationData.chainId && typeof validationData.chainId === "number";
      const hasValidContractAddress =
        validationData.contractAddress &&
        typeof validationData.contractAddress === "string" &&
        (validationData.contractAddress as string).startsWith("0x") &&
        (validationData.contractAddress as string).length === 42;
      prerequisitesMet = !!(hasChainId && hasValidContractAddress);
    }

    // Hide if prerequisites are not met, or if in auto mode
    if (!prerequisitesMet || abiSelectorMode === "auto") {
      return <></>;
    }
  }

  // Token amount fields - use TokenAmountInput with token context
  // Detected via schema format: "token-amount" (or legacy field name "amount" with tokenAddress in schema)
  // Supports "x-token-field" to specify which field provides token address (defaults to "tokenAddress")
  // Supports "x-chain-field" to specify which field provides chain ID (defaults to "chainId")
  // Supports "x-token-address" to hardcode a token address directly on the field
  // Supports "x-chain-id" to hardcode a chain ID directly on the field
  const isTokenAmountField =
    property.type === "token-amount" ||
    property.format === "token-amount" ||
    (name === "amount" && schemaProperties.tokenAddress);

  if (isTokenAmountField) {
    const tokenFieldName = property["x-token-field"] || "tokenAddress";
    const chainFieldName = property["x-chain-field"] || "chainId";
    let tokenResolve = property["x-token-resolve"] as "erc4626" | "erc4626-vault" | undefined;

    // When x-token-resolve-switch is set, check the referenced field to override resolve mode.
    // Currently only morpho-vault-withdraw uses this: withdrawType === "shares" → vault's own decimals.
    // To support other switch values, encode the mapping in the schema annotation instead.
    const switchField = property["x-token-resolve-switch"] as string | undefined;
    if (switchField && allValues[switchField] === "shares") {
      tokenResolve = "erc4626-vault";
    }

    const resolvedTokenAddress =
      property["x-token-address"] || validationData[tokenFieldName] || allValues[tokenFieldName];
    const resolvedChainId = property["x-chain-id"] || validationData[chainFieldName] || allValues[chainFieldName];

    return withVariableToggle(
      <TokenAmountInput
        label={hideLabel ? "" : property.title || "Amount"}
        description={hideLabel ? "" : property.description}
        value={value || ""}
        onChange={handleChange}
        required={property.required || false}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
        tokenAddress={resolvedTokenAddress}
        chainId={resolvedChainId}
        tokenResolve={tokenResolve}
        rawOutput={!currentNodeId}
      />,
    );
  }

  // Not a blockchain field — return null to signal "not handled"
  return null;
}
